import {
  exclusionContract,
  ExclusionDataArraySchema,
  type ExclusionDataSchemaType,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api/parsedApi";
import { useQuery } from "@tanstack/react-query";

export type Exclusion = ExclusionDataSchemaType;

const queryParamsSchema = exclusionContract.findAll.query;

export interface UseWorkerBlockedStatusParams {
  workplaceId: string;
  workerId: string;
  enabled?: boolean;
}

export interface WorkerBlockedStatus {
  isBlocked: boolean;
  exclusion?: Exclusion;
}

/**
 * Hook to check if a worker is blocked/excluded from a workplace.
 * Returns the blocked status and exclusion details if blocked.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useWorkerBlockedStatus({
 *   workplaceId: "facility123",
 *   workerId: "worker456",
 * });
 *
 * if (data?.isBlocked) {
 *   return <ChatBlockedState exclusion={data.exclusion} />;
 * }
 * ```
 */
export function useWorkerBlockedStatus(params: UseWorkerBlockedStatusParams) {
  const { workplaceId, workerId, enabled = true } = params;

  return useQuery({
    queryKey: ["chat", "exclusion", workplaceId, workerId],
    queryFn: async (): Promise<WorkerBlockedStatus> => {
      const response = await get({
        url: "/exclusion",
        queryParams: { facilityId: workplaceId, agentId: workerId },
        queryParamsSchema,
        responseSchema: ExclusionDataArraySchema,
      });

      const exclusion = response.data[0];

      return {
        isBlocked: isDefined(exclusion),
        exclusion,
      };
    },
    enabled: enabled && Boolean(workplaceId) && Boolean(workerId),
  });
}
